import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useDispatch } from 'react-redux'

import {
  recipeFetcher,
  type MenuURN,
  type RecipeCustomisations,
  type RecipeQueryResponse,
  type RecipeURN,
} from '@library/api-menu-experience'
import type { CollectionUUID, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'

type IngredientCustomisations = Extract<RecipeCustomisations, { type: 'ingredients' }>
type CustomisableRecipe = RecipeQueryResponse & { customisations: IngredientCustomisations }

export type RecipeCustomisationPanelData = {
  customisations?: IngredientCustomisations
  customisationRecipe?: RecipeQueryResponse
  collectionId?: CollectionUUID
  menuId?: string | null
  selectedRecipeId?: RecipeUUID
  isLoadingCustomisations: boolean
  hasCustomisationError: boolean
  isCustomisationSelectionPending?: boolean
  hasCustomisationSelectionError?: boolean
  hasOpenedCustomisations: boolean
}

type RecipeIdentifiers = {
  recipeId: RecipeUUID
  recipeUrn?: RecipeURN
}

type OpenPanel = {
  originalRecipeId: RecipeUUID
  selectedRecipeId: RecipeUUID
  selectedRecipeUrn?: RecipeURN
  numPortions: number
  recipePosition?: number
}

type LoadedPanel = OpenPanel & {
  recipe: CustomisableRecipe
  failedRecipeIdentifiers?: RecipeIdentifiers
}

type PanelState =
  | { status: 'closed' }
  | (OpenPanel & { status: 'loading' })
  | (OpenPanel & { status: 'error' })
  | (LoadedPanel & { status: 'ready' })
  | (LoadedPanel & { status: 'selecting' })

type PreparedRecipeSelection = {
  commit: () => void
  selectedRecipeId: RecipeUUID
  selectedRecipeUrn?: RecipeURN
}

type PrepareRecipeSelection = (
  originalRecipeId: RecipeUUID,
  recipeId: RecipeUUID,
  numPortions: number,
  recipeUrn?: RecipeURN,
) => Promise<PreparedRecipeSelection>

type UseRecipeCustomisationPanelArgs = {
  boxNumPortions: number
  collectionId?: string
  deliveryDate: string
  menuId: string
  menuUrn?: MenuURN
  getNumPortionsForRecipeId: (recipeId: RecipeUUID) => number
  getSelectedRecipeIdentifiers: (
    originalRecipeId: RecipeUUID,
    originalRecipeUrn?: RecipeURN,
  ) => RecipeIdentifiers
  /**
   * Prepares any external UI state needed for a selection. The panel does not need to know that
   * this currently prepares a recipe-grid tile; it only decides when the result can be committed.
   */
  prepareRecipeSelection: PrepareRecipeSelection
}

type TrackRecipeAlternativeOptionsMenuOpen = {
  action: 'recipe-alternative-options-menu-open'
  recipeId: RecipeUUID
  collectionId: string
}

type TrackClickCustomise = {
  action: 'click_customise'
  properties: {
    collection_id: string
    recipe_id: RecipeUUID
    recipe_uuid: RecipeUUID
    menu_uuid: string | null
    event_screen: 'view_recipe_list_screen'
    recipe_list_position?: number
  }
}

const idlePanelData: RecipeCustomisationPanelData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

const hasIngredientCustomisationOptions = (
  recipe: RecipeQueryResponse,
): recipe is CustomisableRecipe =>
  recipe.customisations.type === 'ingredients' &&
  recipe.customisations.slots.some((slot) => slot.values.length > 1)

const getOpenRecipeId = (panelState: PanelState) =>
  panelState.status === 'closed' ? null : panelState.originalRecipeId

export const useRecipeCustomisationPanel = ({
  boxNumPortions,
  collectionId,
  deliveryDate,
  menuId,
  menuUrn,
  getNumPortionsForRecipeId,
  getSelectedRecipeIdentifiers,
  prepareRecipeSelection,
}: UseRecipeCustomisationPanelArgs) => {
  const dispatch = useDispatch()
  const [panelState, setPanelState] = useState<PanelState>({ status: 'closed' })
  const panelRequestIdRef = useRef(0)
  const panelContextKeyRef = useRef('')
  const detailsCacheRef = useRef(new Map<string, Promise<RecipeQueryResponse>>())
  const panelContextKey = `${menuUrn ?? menuId}:${deliveryDate}:${boxNumPortions}:${
    collectionId ?? ''
  }`

  panelContextKeyRef.current = panelContextKey

  useEffect(() => {
    panelRequestIdRef.current += 1
    detailsCacheRef.current.clear()
    setPanelState({ status: 'closed' })
  }, [panelContextKey])

  const isCurrentPanelRequest = useCallback(
    (requestId: number, requestPanelContextKey: string) =>
      panelRequestIdRef.current === requestId &&
      panelContextKeyRef.current === requestPanelContextKey,
    [],
  )

  const fetchRecipeDetails = useCallback(
    ({ recipeId, recipeUrn }: RecipeIdentifiers, numPortions: number) => {
      const cacheKey = `${panelContextKey}:${recipeUrn ?? recipeId}:${numPortions}`
      const cachedRequest = detailsCacheRef.current.get(cacheKey)

      if (cachedRequest) {
        return cachedRequest
      }

      const recipeDetailsPromise: Promise<RecipeQueryResponse> = recipeFetcher({
        menuId,
        menuUrn,
        recipeId,
        recipeUrn,
        numPortions,
        deliveryDate,
        collectionId,
      }).finally(() => {
        if (detailsCacheRef.current.get(cacheKey) === recipeDetailsPromise) {
          detailsCacheRef.current.delete(cacheKey)
        }
      })

      detailsCacheRef.current.set(cacheKey, recipeDetailsPromise)

      return recipeDetailsPromise
    },
    [collectionId, deliveryDate, menuId, menuUrn, panelContextKey],
  )

  const closeRecipeCustomisations = useCallback(() => {
    panelRequestIdRef.current += 1
    setPanelState({ status: 'closed' })
  }, [])

  const closeRecipeCustomisationsForRecipe = useCallback((recipeId: RecipeUUID) => {
    setPanelState((current) => {
      if (current.status === 'closed' || current.selectedRecipeId !== recipeId) {
        return current
      }

      panelRequestIdRef.current += 1

      return { status: 'closed' }
    })
  }, [])

  const openRecipeCustomisations = useCallback(
    async (
      originalRecipeId: RecipeUUID,
      originalRecipeUrn?: RecipeURN,
      recipePosition?: number,
    ) => {
      const { recipeId: selectedRecipeId, recipeUrn: selectedRecipeUrn } =
        getSelectedRecipeIdentifiers(originalRecipeId, originalRecipeUrn)
      const selectedNumPortions = getNumPortionsForRecipeId(selectedRecipeId)
      panelRequestIdRef.current += 1
      const requestId = panelRequestIdRef.current
      const requestPanelContextKey = panelContextKey

      setPanelState({
        status: 'loading',
        originalRecipeId,
        selectedRecipeId,
        selectedRecipeUrn,
        numPortions: selectedNumPortions,
        recipePosition,
      })

      if (collectionId) {
        queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
          action: 'recipe-alternative-options-menu-open',
          recipeId: selectedRecipeId,
          collectionId,
        })
        queueTrackingEvent<TrackClickCustomise>({
          action: 'click_customise',
          properties: {
            collection_id: collectionId,
            recipe_id: selectedRecipeId,
            recipe_uuid: selectedRecipeId,
            menu_uuid: menuId,
            event_screen: 'view_recipe_list_screen',
            recipe_list_position: recipePosition,
          },
        })
      }

      try {
        const recipe = await fetchRecipeDetails(
          { recipeId: selectedRecipeId, recipeUrn: selectedRecipeUrn },
          selectedNumPortions,
        )

        if (!isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          return
        }

        if (!hasIngredientCustomisationOptions(recipe)) {
          setPanelState({
            status: 'error',
            originalRecipeId,
            selectedRecipeId,
            selectedRecipeUrn,
            numPortions: selectedNumPortions,
            recipePosition,
          })

          return
        }

        setPanelState({
          status: 'ready',
          originalRecipeId,
          selectedRecipeId,
          selectedRecipeUrn,
          numPortions: selectedNumPortions,
          recipePosition,
          recipe,
        })
      } catch {
        if (isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          setPanelState({
            status: 'error',
            originalRecipeId,
            selectedRecipeId,
            selectedRecipeUrn,
            numPortions: selectedNumPortions,
            recipePosition,
          })
        }
      }
    },
    [
      collectionId,
      fetchRecipeDetails,
      getNumPortionsForRecipeId,
      getSelectedRecipeIdentifiers,
      isCurrentPanelRequest,
      menuId,
      panelContextKey,
    ],
  )

  const selectRecipeCustomisation = useCallback(
    async (originalRecipeId: RecipeUUID, selection: RecipeIdentifiers, recipePosition?: number) => {
      if (
        panelState.status !== 'ready' ||
        panelState.originalRecipeId !== originalRecipeId ||
        !menuId ||
        !deliveryDate
      ) {
        return
      }

      const previousPanel = panelState
      const selectedNumPortions = getNumPortionsForRecipeId(selection.recipeId)
      const selectedValues = panelState.recipe.customisations.slots
        .flatMap(({ values }) => values)
        .filter(({ recipeId }) => recipeId === selection.recipeId)

      panelRequestIdRef.current += 1
      const requestId = panelRequestIdRef.current
      const requestPanelContextKey = panelContextKey

      setPanelState({
        ...previousPanel,
        status: 'selecting',
        failedRecipeIdentifiers: undefined,
      })

      try {
        // Panel details and grid tiles come from separate requests today. Prepare the tile without
        // applying it so neither UI changes unless both requests succeed and this request is current.
        const [recipe, preparedSelection] = await Promise.all([
          fetchRecipeDetails(selection, selectedNumPortions),
          prepareRecipeSelection(
            originalRecipeId,
            selection.recipeId,
            selectedNumPortions,
            selection.recipeUrn,
          ),
        ])

        if (!isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          return
        }

        if (!hasIngredientCustomisationOptions(recipe)) {
          throw new Error('Selected recipe does not contain ingredient customisations')
        }

        preparedSelection.commit()
        if (collectionId) {
          dispatch({
            type: actionTypes.TRACKING,
            trackingData: {
              actionType: trackingKeys.selectRecipeVariant,
              recipe_id: originalRecipeId,
              recipe_variant_id: preparedSelection.selectedRecipeId,
              collection_id: collectionId,
              variant_out_of_stock:
                !recipe.isAvailable || selectedValues.some(({ isAvailable }) => !isAvailable),
              view: 'grid',
              has_surcharge: Boolean(recipe.pricing?.perPortionSurcharge),
            },
          })
        }
        setPanelState({
          status: 'ready',
          originalRecipeId,
          selectedRecipeId: preparedSelection.selectedRecipeId,
          selectedRecipeUrn: preparedSelection.selectedRecipeUrn,
          numPortions: selectedNumPortions,
          recipePosition,
          recipe,
        })
      } catch {
        datadogRum.addAction('menu_experience_recipe_customisation_selection_failed', {
          menu_id: menuId,
          original_recipe_id: originalRecipeId,
          recipe_id: selection.recipeId,
          num_portions: selectedNumPortions,
        })

        if (isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          setPanelState({
            ...previousPanel,
            status: 'ready',
            failedRecipeIdentifiers: selection,
          })
        }
      }
    },
    [
      collectionId,
      deliveryDate,
      dispatch,
      fetchRecipeDetails,
      getNumPortionsForRecipeId,
      isCurrentPanelRequest,
      menuId,
      panelContextKey,
      panelState,
      prepareRecipeSelection,
    ],
  )

  useEffect(() => {
    if (panelState.status !== 'ready') {
      return
    }

    const currentNumPortions = getNumPortionsForRecipeId(panelState.selectedRecipeId)

    if (currentNumPortions === panelState.numPortions) {
      return
    }

    const previousPanel = panelState
    panelRequestIdRef.current += 1
    const requestId = panelRequestIdRef.current
    const requestPanelContextKey = panelContextKey

    setPanelState({ ...panelState, status: 'selecting' })
    void fetchRecipeDetails(
      { recipeId: panelState.selectedRecipeId, recipeUrn: panelState.selectedRecipeUrn },
      currentNumPortions,
    )
      .then((recipe) => {
        if (!isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          return
        }

        setPanelState(
          hasIngredientCustomisationOptions(recipe)
            ? { ...previousPanel, recipe, numPortions: currentNumPortions }
            : {
                status: 'error',
                originalRecipeId: panelState.originalRecipeId,
                selectedRecipeId: panelState.selectedRecipeId,
                selectedRecipeUrn: panelState.selectedRecipeUrn,
                numPortions: currentNumPortions,
                recipePosition: panelState.recipePosition,
              },
        )
      })
      .catch(() => {
        if (isCurrentPanelRequest(requestId, requestPanelContextKey)) {
          setPanelState({
            status: 'error',
            originalRecipeId: panelState.originalRecipeId,
            selectedRecipeId: panelState.selectedRecipeId,
            selectedRecipeUrn: panelState.selectedRecipeUrn,
            numPortions: currentNumPortions,
            recipePosition: panelState.recipePosition,
          })
        }
      })
  }, [
    fetchRecipeDetails,
    getNumPortionsForRecipeId,
    isCurrentPanelRequest,
    panelContextKey,
    panelState,
  ])

  const retryRecipeCustomisation = useCallback(() => {
    if (panelState.status !== 'ready' || !panelState.failedRecipeIdentifiers) {
      return
    }

    void selectRecipeCustomisation(
      panelState.originalRecipeId,
      panelState.failedRecipeIdentifiers,
      panelState.recipePosition,
    )
  }, [panelState, selectRecipeCustomisation])

  const activeRecipeId = getOpenRecipeId(panelState)

  const activeRecipeState = useMemo<RecipeCustomisationPanelData>(() => {
    if (panelState.status === 'closed') {
      return idlePanelData
    }

    if (panelState.status === 'loading') {
      return {
        isLoadingCustomisations: true,
        hasCustomisationError: false,
        hasOpenedCustomisations: true,
      }
    }

    if (panelState.status === 'error') {
      return {
        isLoadingCustomisations: false,
        hasCustomisationError: true,
        hasOpenedCustomisations: true,
      }
    }

    return {
      customisations: panelState.recipe.customisations,
      customisationRecipe: panelState.recipe,
      collectionId: collectionId as CollectionUUID | undefined,
      menuId,
      selectedRecipeId: panelState.selectedRecipeId,
      isLoadingCustomisations: false,
      hasCustomisationError: false,
      isCustomisationSelectionPending: panelState.status === 'selecting',
      hasCustomisationSelectionError: Boolean(panelState.failedRecipeIdentifiers),
      hasOpenedCustomisations: true,
    }
  }, [collectionId, menuId, panelState])

  const getRecipeCustomisationData = useCallback(
    (recipeId: RecipeUUID): RecipeCustomisationPanelData =>
      recipeId === activeRecipeId ? activeRecipeState : idlePanelData,
    [activeRecipeId, activeRecipeState],
  )

  return {
    activeRecipeId,
    closeRecipeCustomisations,
    closeRecipeCustomisationsForRecipe,
    getRecipeCustomisationData,
    openRecipeCustomisations,
    retryRecipeCustomisation,
    selectRecipeCustomisation,
  }
}
